import { formatRefreshedAgo } from './refreshedLabel';

const campaignsNoun = (n) => `${n} ${n === 1 ? 'campaign' : 'campaigns'}`;

/**
 * The account's monthly optimization cap, as one line for the page: how many
 * distinct campaigns have been optimized this month against the plan's limit.
 * `cap` is the API's { limit, used } (limit null = unlimited). '' when there is
 * nothing to say.
 */
export function describeCampaignCap(cap) {
  if (!cap || cap.limit == null) return '';
  const base = `Your plan covers ${campaignsNoun(cap.limit)} a month; ${cap.used} optimized this month.`;
  return cap.used >= cap.limit
    ? `${base} Campaigns already optimized this month can be refreshed again. To optimize more campaigns, contact support to upgrade your plan.`
    : base;
}

/**
 * Why some campaigns in a refresh were not analysed because of the monthly cap,
 * or ''. Reads the refresh summary's cappedMonthly / monthlyCap.
 */
export function campaignCapNote(result) {
  const n = result?.cappedMonthly || 0;
  if (!n) return '';
  const limit = result.monthlyCap?.limit;
  return `${campaignsNoun(n)} ${n === 1 ? 'was' : 'were'} not analysed: `
    + `${limit != null ? `your plan covers ${campaignsNoun(limit)} a month, and all are in use` : 'your plan\'s monthly campaign limit is reached'}. `
    + 'To optimize more campaigns, contact support to upgrade your plan.';
}

/**
 * Throw before any stats pull starts when the cap would turn away every
 * selected campaign: all of them are new to this month and no slot is left.
 * A selection with even one campaign the cap allows goes ahead, and the server
 * skips (and reports) the rest.
 */
export function assertCampaignCapAllows(cap, campaignIds) {
  if (!cap || cap.limit == null || cap.used < cap.limit) return;
  const counted = new Set(cap.campaignIds || []);
  if (campaignIds.some(id => counted.has(id))) return;
  throw new Error(`Your plan covers ${campaignsNoun(cap.limit)} a month, and all are in use, so `
    + `${campaignIds.length === 1 ? 'this campaign' : 'these campaigns'} cannot be optimized this month. `
    + 'To optimize more campaigns, contact support to upgrade your plan.');
}

/**
 * What a refresh actually did, in one sentence.
 *
 * The interesting case is ZERO DISPATCHED, which has several very different
 * causes and one useless rendering ("0 dispatched"). A user who pressed Refresh
 * a minute after a run, a user whose campaigns the DSP says are paused, and a
 * user whose account has never had a grid pull all get the same number and need
 * different answers — the first should press Force, the other two should not.
 *
 * Exported for its own test: this is the only place those distinctions are made.
 */
export function describeRefresh(result) {
  if (!result) return '';
  if (!result.ok) return result.error || 'Failed to refresh analyses.';

  const scope = result.scope === 'campaign' ? 'this campaign' : 'this account';
  if (result.dispatched > 0) {
    const failed = result.failed
      ? ` ${result.failed} failed to dispatch.`
      : '';
    const capped = campaignCapNote(result);
    return `Started ${result.dispatched} ${result.dispatched === 1 ? 'analysis' : 'analyses'} `
      + `across ${result.live} ${result.live === 1 ? 'campaign' : 'campaigns'} on ${scope}. `
      + `Results appear here as each one completes — usually a few minutes.${failed}${capped ? ` ${capped}` : ''}`;
  }

  // Nothing was dispatched. ORDER IS THE POINT, and it is most-specific-first,
  // not most-obvious-first. skippedNoUser and the caps BOTH leave live at 0
  // (planDispatches drops those candidates before the live list), so a
  // "none are live" branch placed above them swallows both and tells the user
  // to go reload a grid that was never the problem. The monthly cap comes
  // first: it is the one the user cannot fix by waiting or forcing.
  if (result.cappedMonthly > 0) return `Nothing analysed. ${campaignCapNote(result)}`;
  if (result.skippedAlreadyToday > 0) {
    return `Nothing to refresh: all ${result.live} live `
      + `${result.live === 1 ? 'campaign was' : 'campaigns were'} already analysed in the last `
      + '20 hours. Use Force re-run to analyse them again anyway.';
  }
  if (result.skippedNoUser > 0) {
    return 'Nothing to refresh: the account has no admin to attribute the runs to.';
  }
  if (result.cappedPerAccount > 0 || result.cappedPerTick > 0) {
    return 'Nothing to refresh: the per-run cap was already reached '
      + `(${result.cappedPerAccount} capped for this account, `
      + `${result.cappedPerTick} for this run).`;
  }
  if (result.candidates === 0) {
    return `Nothing to refresh: no campaigns on ${scope} are eligible — a campaign needs a `
      + 'supported destination, a launched platform id, and a status that is not archived, '
      + 'draft, failed or stopped.';
  }
  if (result.live === 0) {
    const notLive = result.skippedNotLive || 0;
    const unknown = result.skippedUnknown || 0;
    return `Nothing to refresh: none of the ${result.candidates} campaigns on ${scope} are `
      + `confirmed live in their DSP (${notLive} reported paused or finished, ${unknown} have `
      + 'no reported status yet). Reload the campaign grid to pull current statuses.';
  }
  return `Nothing to refresh on ${scope}.`;
}

/**
 * How long the Optimization workspace's Refresh keeps checking on the analyses it
 * started, and how often.
 *
 * The SERVER decides when an analysis is over: SingleMessageProcessingService
 * (app-backend, every 5s) flips each one to completed when its result lands, and
 * flags an optimizer analysis 'timeout' once it is TIMEOUT_CONFIGS['optimize-
 * campaign'] = 7200s old — counted from dispatch, so queue time counts, and even
 * then a late result is still accepted. The browser's flat 20-minute cut-off never
 * matched that two-hour limit, and big refreshes legitimately run for over an
 * hour: they queue behind the optimizer's few workers. Reported 2026-10-04: 21 of
 * 105 analyses had landed at 20 minutes (about one a minute) and the panel said
 * "still pending" while the rest ran on.
 *
 * So the refresh keeps checking until every analysis has an answer, and stops on
 * its own only a little after the server's limit, reporting whatever is still out
 * as possibly late rather than as failed.
 */
export const ANALYSIS_SERVER_TIMEOUT_MS = 120 * 60 * 1000;
export const ANALYSIS_GIVE_UP_MS = ANALYSIS_SERVER_TIMEOUT_MS + 10 * 60 * 1000;
/** After this long, the progress line says a big refresh can take over an hour. */
export const LONG_REFRESH_NOTICE_MS = 20 * 60 * 1000;

/**
 * The wait before the next check, `elapsedMs` after the analyses were started.
 * Frequent while results land fast, then backing off: each check re-reads the
 * account's runs and every still-pending analysis, so a two-hour wait at a flat
 * 15s would be ~480 rounds of those from one open tab.
 */
export function analysisPollDelay(elapsedMs) {
  if (elapsedMs < 5 * 60 * 1000) return 15 * 1000;
  if (elapsedMs < LONG_REFRESH_NOTICE_MS) return 30 * 1000;
  return 60 * 1000;
}

/**
 * When the analyses shown on the page were last produced, in epoch ms.
 *
 * The newest `completedAt` across the runs GET /runs returned — i.e. "when did
 * anything last land", the same question the campaign grid answers with
 * metrics_fetched_at (see utils/refreshedLabel.newestMetricsFetchedAt, and the
 * argument there for newest-not-oldest). Null when nothing has ever completed,
 * which the caller renders as nothing rather than as "Refreshed 55y ago".
 */
export function newestRunCompletedAt(runs) {
  let newest = null;
  for (const r of runs || []) {
    if (!r || !r.completedAt) continue;
    const t = new Date(r.completedAt).getTime();
    if (Number.isNaN(t)) continue;
    if (newest === null || t > newest) newest = t;
  }
  return newest;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * How old the analyses behind a page or an export are — newest AND oldest.
 *
 * The newest run alone answers "did anything land", but a page where one
 * campaign was analysed today and thirty last week reads as fresh. `older`
 * counts the runs that completed more than a day before the newest, so that
 * spread is stated instead of hidden. Null when nothing has completed.
 *
 * @param {Array<{completedAt?: string|null}>} runs
 * @returns {{newest:number, oldest:number, count:number, older:number}|null}
 */
export function runFreshness(runs) {
  const times = (runs || [])
    .map((r) => (r && r.completedAt ? new Date(r.completedAt).getTime() : NaN))
    .filter((t) => !Number.isNaN(t));
  if (!times.length) return null;
  const newest = Math.max(...times);
  return {
    newest,
    oldest: Math.min(...times),
    count: times.length,
    older: times.filter((t) => t < newest - DAY_MS).length,
  };
}

/** "8 Oct, 11:52" in the viewer's time zone; the year only when it is not this one.
 *  Built by hand rather than with toLocaleString so it reads the same on every
 *  browser and test runner. */
export function formatAnalysisDate(ms, now = Date.now()) {
  const d = new Date(ms);
  const year = d.getFullYear() === new Date(now).getFullYear() ? '' : ` ${d.getFullYear()}`;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${year}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The page's freshness line: "Data refreshed 8 Oct, 11:52 · 3d ago", plus
 * "· 6 analyses older, from 2 Sep" when the runs shown span more than a day.
 * `stale` is set once the newest analysis is more than a day old, so the line
 * can stand out instead of reading as a muted hint.
 */
export function describeDataFreshness(freshness, now = Date.now()) {
  if (!freshness) return null;
  const ago = formatRefreshedAgo(freshness.newest).replace(/^Refreshed /, '');
  const older = freshness.older
    ? ` · ${freshness.older} ${freshness.older === 1 ? 'analysis' : 'analyses'} older, from ${formatAnalysisDate(freshness.oldest, now).split(',')[0]}`
    : '';
  return {
    text: `Data refreshed ${formatAnalysisDate(freshness.newest, now)} · ${ago}${older}`,
    stale: now - freshness.newest > DAY_MS,
  };
}

/**
 * The line shown while a refresh's analyses are landing, and when they have.
 *
 * A press dispatches N analyses that complete asynchronously over minutes. A
 * client who pressed Refresh and is waiting on the page needs to be told two
 * things this component otherwise never says: that it is still working, and
 * that it has finished. "Results appear as each one completes" is not an answer
 * to "is it done yet".
 */
export function describeRefreshProgress(progress) {
  if (!progress) return '';
  const { dispatched, completed, done, timedOut } = progress;
  const noun = (n) => (n === 1 ? 'analysis' : 'analyses');
  if (done) {
    return `Refresh complete — ${dispatched} ${noun(dispatched)} finished. `
      + 'Everything shown is up to date.';
  }
  if (timedOut) {
    return `${completed} of ${dispatched} ${noun(dispatched)} finished after 20 minutes; `
      + 'the rest are still running. Reload the page later to see them.';
  }
  return `Refreshing… ${completed} of ${dispatched} ${noun(dispatched)} complete. `
    + 'Results appear as each one finishes.';
}

const performanceRefresh = {
  describeRefresh, newestRunCompletedAt, runFreshness, describeDataFreshness, formatAnalysisDate, describeRefreshProgress, analysisPollDelay,
  ANALYSIS_SERVER_TIMEOUT_MS, ANALYSIS_GIVE_UP_MS, LONG_REFRESH_NOTICE_MS,
};

export default performanceRefresh;
